import React, { useState } from 'react';
import { MyActiveMicrobesResponse } from '@viome/viome-base/src/usecase/Health/Health.model';
import { StandardContainer } from '@design-system/StandardContainer';
import { NavBar } from '@design-system/NavBar';
import { Skeleton } from '@design-system/Skeleton';
import { ToggleGroup, ToggleGroupItem } from '@design-system/ToggleGroup';
import { ScrollArea, ScrollBar } from '@design-system/ScrollArea';
import { Separator } from '@design-system/Separator';
import { EventTracker } from '@/utils';

interface ActiveMicrobesProps {
  activeMicrobes?: MyActiveMicrobesResponse;
  isLoading?: boolean;
  'data-testid'?: string;
}

export const ActiveMicrobesContainer: React.FC<ActiveMicrobesProps> = ({
  activeMicrobes,
  isLoading = false,
  'data-testid': testId,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    undefined,
  );

  const title = activeMicrobes?.title || 'Loading...';
  const about = activeMicrobes?.about || 'Loading description...';
  const microbes = activeMicrobes?.active_microbes || [];

  const categories = Array.from(
    new Set(microbes.map((microbe) => microbe.super_kingdom)),
  );

  const filteredMicrobes = selectedCategory
    ? microbes.filter((microbe) => microbe.super_kingdom === selectedCategory)
    : microbes;

  const renderCategoryOptions = () => {
    if (isLoading) {
      return Array.from({ length: 4 }).map((_, index) => (
        <Skeleton
          key={`skeleton-category-${index}`}
          data-testid={`${testId}-categorySkeleton-${index}`}
          className="w-28 h-8 mx-1 my-2"
        />
      ));
    }

    if (!categories.length) {
      return (
        <p 
          data-testid={`${testId}-noCategoriesMessage`}
          className="typography-caption text-secondary"
        >
          No categories
        </p>
      );
    }

    return categories.map((category, index) => (
      <ToggleGroupItem
        key={category}
        data-testid={`${testId}-categoryOption-${index}`}
        value={category}
        className="px-4 py-2 bg-[#42434D73]"
      >
        {category}
      </ToggleGroupItem>
    ));
  };

  const renderMicrobes = () => {
    if (isLoading) {
      return Array.from({ length: 6 }).map((_, index) => (
        <Skeleton
          key={`skeleton-microbe-${index}`}
          data-testid={`${testId}-microbeSkeleton-${index}`}
          className="w-full h-20 rounded-lg"
        />
      ));
    }

    if (!filteredMicrobes.length) {
      return (
        <p 
          data-testid={`${testId}-noMicrobesMessage`}
          className="typography-body text-secondary"
        >
          No microbes found in the selected category.
        </p>
      );
    }

    return filteredMicrobes.map((microbe, index) => (
      <div
        key={`${microbe.name}-${index}`}
        data-testid={`${testId}-microbeItem-${index}`}
        className="p-4 rounded-lg shadow-md"
      >
        <p 
          data-testid={`${testId}-microbeName-${index}`}
          className="dark typography-btn-large text-primary line-clamp-2"
        >
          {microbe.name}
        </p>
        <p 
          data-testid={`${testId}-microbeKingdom-${index}`}
          className="dark typography-caption1 text-secondary mt-2"
        >
          {microbe.super_kingdom}
        </p>
        <Separator 
          data-testid={`${testId}-microbeSeparator-${index}`}
          className="border-stroke-primary mt-4" 
        />
      </div>
    ));
  };

  return (
    <div data-testid={testId} className="min-h-screen">
      <StandardContainer data-testid={`${testId}-standardContainer`}>
        <NavBar 
          data-testid={`${testId}-navbar`}
          returnButtonTitle="Health" 
          className="md:-ml-5 mt-12" 
        />

        {/* Page Header */}
        <div 
          data-testid={`${testId}-content`}
          className="mt-12"
        >
          {isLoading ? (
            <div 
              data-testid={`${testId}-headerSkeleton`}
              className="mb-6"
            >
              <Skeleton 
                data-testid={`${testId}-titleSkeleton`}
                className="w-3/4 h-2 mb-4" 
              />
              <Skeleton 
                data-testid={`${testId}-aboutSkeleton`}
                className="w-full h-6 max-w-[37rem]" 
              />
            </div>
          ) : (
            <>
              <h1 
                data-testid={`${testId}-title`}
                className="dark typography-h1 text-primary mb-4"
              >
                {title}
              </h1>
              <p 
                data-testid={`${testId}-about`}
                className="dark typography-body text-secondary max-w-[37rem] mb-6"
              >
                {about}
              </p>
            </>
          )}

          {/* Category Selector */}
          <ScrollArea data-testid={`${testId}-categorySelectorScrollArea`}>
            <ToggleGroup
              data-testid={`${testId}-categorySelector`}
              type="single"
              variant="outline"
              value={selectedCategory}
              onValueChange={(data) => {
                EventTracker.trackEvent(
                  'health',
                  `microbes_filter_${selectedCategory?.toLocaleLowerCase()}`,
                );
                setSelectedCategory(data);
              }}
              className="dark flex-nowrap whitespace-nowrap"
            >
              {renderCategoryOptions()}
            </ToggleGroup>
            <ScrollBar 
              data-testid={`${testId}-categorySelectorScrollBar`}
              orientation="horizontal" 
            />
          </ScrollArea>

          {/* Microbes Grid */}
          <div 
            data-testid={`${testId}-microbesGrid`}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 mt-6"
          >
            {renderMicrobes()}
          </div>
        </div>
      </StandardContainer>
    </div>
  );
};
